import _ from 'lodash';
import React, { useMemo, useRef } from 'react';
import { makeStyles } from 'design-system/styles';
// config definitions
import { definitions } from './NiceConfigsDictionary';
import { IConfig, IBaseConfig } from './NiceConfigsSections';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import SanitizedMarkdown from 'design-system/SanitizedMarkdown';
import { Banner } from '@scale/scaleui';
import {
  GenAIOpsHubPaths,
  MainSidebarMenu,
  QualityMenu,
  LinterTabs,
} from 'react-scale/corp/views/CorpProjectGroupManager/constants';

// UI
const DictionaryEntry = (props: { config: IConfig }) => {
  const { config } = props;

  // eslint-disable-next-line
  const classes = useStyles();

  const info = _.get(definitions, config.path, { definition: '', advice: '' });
  let { definition } = info;
  const { advice } = info;

  definition = definition === ' ' || !definition ? 'No definition...yet' : definition;

  return (
    <div className={classes.dictionary}>
      <p className={classes.dictText} style={{ fontWeight: 500 }}>
        {config.displayLabel
          ? config.displayLabel
          : _.upperFirst(_.lowerCase(_.startCase(config.name)))}
      </p>
      <p className={classes.dictText}>
        <span className={classes.dictLabel}>Definition:</span>{' '}
        {config.path.includes('responseLinterConfigRules') ? (
          <SanitizedMarkdown text={definition} />
        ) : (
          definition
        )}
      </p>
      {advice && (
        <p className={classes.dictText}>
          <span className={classes.dictLabel}>General advice:</span> {advice}
        </p>
      )}
    </div>
  );
};

interface SectionUIProps {
  title: string;
  description: string;
  configs: Array<IConfig | Array<IConfig>>;
  changes?: Record<string, any>;
  handleChange: (path: string, newValue: any) => void;
  displayBanner?: boolean;
  projectId: string;
}

export const SectionUI: React.FC<SectionUIProps> = props => {
  // focused config is set on hover of a config (renders definition)
  const [configFocused, setFocus] = React.useState<IConfig | undefined>(undefined);
  const [showBanner, setShowBanner] = React.useState(true);

  const handleFocus = (config: IConfig | undefined) => {
    setFocus(config);
  };

  const LINTER_CENTER_LINK = `${GenAIOpsHubPaths.Projects}/${props.projectId}/${MainSidebarMenu.Quality}/${QualityMenu.Linters}/${LinterTabs.Configurations}`;
  const renderLinterLibraryBanner = (
    <Banner
      className="scale-theme mt-2 mb-2"
      title="Linter library is live on Linter Center!"
      subtitle={
        'For GenAI Task Chat operators: Please use LLM Control Center: Linter Center to add, configure and test your linters.'
      }
      variant="info"
      cta={{
        label: 'Linter Center',
        onClick: () => window.open(LINTER_CENTER_LINK, '_blank'),
      }}
      onClose={() => setShowBanner(false)}
      background="neutral"
    />
  );

  // eslint-disable-next-line
  const classes = useStyles();

  return props.configs ? (
    <div className={classes.wrapper}>
      <h3 style={{ fontSize: 16, fontWeight: 500, marginTop: 0 }}>{props.title}</h3>
      {props.title === 'Response linter' &&
        showBanner &&
        props.displayBanner &&
        renderLinterLibraryBanner}
      <div className={classes.bodyWrapper}>
        <div className={classes.configsWrapper}>
          {props.configs.map(config => {
            return renderInput(config, props.changes, props.handleChange, handleFocus);
          })}
        </div>
        <div className={classes.rightContent}>
          {configFocused !== undefined ? (
            <DictionaryEntry config={configFocused} />
          ) : (
            <p className={classes.description}>{props.description}</p>
          )}
        </div>
      </div>
    </div>
  ) : (
    <p>NONE</p>
  );
};

const renderInput = (
  config: IConfig | Array<IConfig>,
  changes: Record<string, any> | undefined,
  // pass arrayIndex cause <Array /> needs it (calls handleChange recursively)
  handleChange: (path: string, newValue: any, arrayIndex?: number) => void,
  handleFocus: (config: IConfig | undefined) => void,
) => {
  if (_.isArray(config)) {
    return (
      <Collection
        key={config[0].path + 'Partition'} // idk if this is ok
        config={config}
        changes={changes}
        handleChange={handleChange}
        handleFocus={handleFocus}
      />
    );
  }

  const element = (() => {
    switch (config._type) {
      case 'Boolean':
        return (
          <Toggle
            key={config.path}
            config={config}
            handleChange={handleChange}
            handleFocus={handleFocus}
          />
        );
      case 'Number':
        return (
          <NumberInput
            key={config.path}
            config={config}
            handleChange={handleChange}
            handleFocus={handleFocus}
          />
        );
      case 'Date':
        return (
          <DateInput
            key={config.path}
            config={config}
            handleChange={handleChange}
            handleFocus={handleFocus}
          />
        );
      case 'String':
        // replace once config changes are merged - testing with this for now.
        if (
          config.path ===
            'configSettingsValues.guidedWorkflowDefinitionConfigRules.workflowMarkdown' ||
          config.path === 'configSettingsValues.internalInstruction'
        ) {
          return (
            <MultilineInput
              key={config.path}
              config={config}
              handleChange={handleChange}
              handleFocus={handleFocus}
            />
          );
        } else if (config.enumValues && config.enumValues.length > 0) {
          return (
            <Select
              key={config.path}
              config={config}
              handleChange={handleChange}
              handleFocus={handleFocus}
            />
          );
        } else {
          return (
            <StringInput
              key={config.path}
              config={config}
              handleChange={handleChange}
              handleFocus={handleFocus}
            />
          );
        }
      case 'Mixed':
        return (
          <JsonInput
            key={config.path}
            config={config}
            handleChange={handleChange}
            handleFocus={handleFocus}
          />
        );
      case 'Array':
        // Edge case: If primitive array, don't want to render with Array component, which is meant for schema arrays
        if (!config.children) {
          return (
            <JsonInput
              key={config.path}
              config={config}
              handleChange={handleChange}
              handleFocus={handleFocus}
              expectArray
            />
          );
        }
        return (
          <Array
            key={config.path}
            config={config}
            handleChange={handleChange}
            handleFocus={handleFocus}
          />
        );
    }
  })();

  // default - return the element without colored border
  if (!element || !changes || !(config.path in changes)) {
    return <div key={config.path + 'editWrap'}>{element}</div>;
  }

  // input has been edited - wrap it in colored border
  return (
    <div key={config.path + 'editWrap'} style={{ border: 'solid 1px #DD45D3', borderRadius: 4 }}>
      {element}
    </div>
  );
};

interface IConfigComponent {
  config: IConfig;
  handleChange: (path: string, value: any, arrayIndex?: number) => void;
  handleFocus: (config: IConfig | undefined) => void;
}

const NumberInput: React.FC<IConfigComponent> = props => {
  if (!props.config) return null;

  const getNumberChangeHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
    path: string,
    handleChange: IConfigComponent['handleChange'],
  ) => {
    const value = e.target.value;
    const numberValue = value ? _.toNumber(value) : '';
    // arrayIndex is important since path is no longer unique identifier
    const arrayIndex = props.config.arrayIndex;
    handleChange(path, numberValue, arrayIndex);
  };

  // eslint-disable-next-line
  const classes = useStyles();

  return (
    <div
      className={classes.config}
      key={props.config.name}
      onMouseEnter={() => props.handleFocus(props.config)}
      onMouseLeave={() => props.handleFocus(undefined)}
    >
      <span
        className={classes.label}
        style={{
          opacity:
            props.config.displayLabel &&
            props.config.displayLabel &&
            props.config.displayLabel.includes('(deprecated)')
              ? 0.5
              : 1,
        }}
      >
        {props.config.displayLabel
          ? props.config.displayLabel
          : _.upperFirst(_.lowerCase(_.startCase(props.config.name)))}
      </span>

      <input
        className={classes.configInput}
        type="number"
        step="0.1"
        value={props.config.value || props.config.value === 0 ? props.config.value : ''} // allow 0s
        // @ts-ignore
        onWheel={e => e.target.blur()} // prevent mouse scroll from accidentally changing value
        onChange={e => getNumberChangeHandler(e, props.config.path, props.handleChange)}
      />
    </div>
  );
};

const DateInput: React.FC<IConfigComponent> = props => {
  function checkDateValue(str: string, max: number) {
    if (str.charAt(0) !== '0' || str === '00') {
      const parsedNum = parseInt(str);
      const num = isNaN(parsedNum) || parsedNum <= 0 || parsedNum > max ? 1 : parsedNum;
      str =
        num > parseInt(max.toString().charAt(0)) && num.toString().length === 1
          ? '0' + num
          : num.toString();
    }
    return str;
  }

  const getDateChangeHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
    path: string,
    handleChange: IConfigComponent['handleChange'],
  ) => {
    // automatic delete of slash when deleting the first number following the slash
    const input = /\D\/$/.test(e.target.value)
      ? e.target.value.substr(0, e.target.value.length - 3)
      : e.target.value;
    // replace all non-digits with empty string
    const dateValues = input.split('/').map(function (v) {
      return v.replace(/\D/g, '');
    });
    // adjust dates to be valid, e.g. change 4/31 to 4/30 because 4/31 doesn't exist
    if (dateValues[0]) dateValues[0] = checkDateValue(dateValues[0], 12);
    if (dateValues[1]) dateValues[1] = checkDateValue(dateValues[1], 31);
    const output = dateValues.map(function (v, i) {
      return v.length === 2 && i < 2 ? v + ' / ' : v;
    });
    const formattedDateValue = output.join('').substr(0, 14);

    // FIXME hacky: maintain duplicate state to avoid cursor issues
    setLocalValue(formattedDateValue);
    // arrayIndex is important since path is no longer unique identifier
    const arrayIndex = props.config.arrayIndex;
    handleChange(path, formattedDateValue, arrayIndex);
  };

  const stringifyValue = (date: string | undefined) => {
    return date && moment(date).isValid()
      ? moment(date).format('MM/DD/YYYY').toString().replace(/\D/g, ' / ')
      : date;
  };

  const [localValue, setLocalValue] = React.useState(stringifyValue(props.config.value));
  // FIXME hacky: state can be updated outside of the input, ie when removing from array
  React.useEffect(() => {
    const formattedConfigVal = stringifyValue(props.config.value);
    if (stringifyValue(localValue) !== formattedConfigVal) {
      setLocalValue(formattedConfigVal);
    }
  }, [props.config.value]);

  // eslint-disable-next-line
  const classes = useStyles();
  return (
    <div
      className={classes.config}
      key={props.config.name}
      onMouseEnter={() => props.handleFocus(props.config)}
      onMouseLeave={() => props.handleFocus(undefined)}
    >
      <span
        className={classes.label}
        style={{
          opacity:
            props.config.displayLabel &&
            props.config.displayLabel &&
            props.config.displayLabel.includes('(deprecated)')
              ? 0.5
              : 1,
        }}
      >
        {props.config.displayLabel
          ? props.config.displayLabel
          : _.upperFirst(_.lowerCase(_.startCase(props.config.name)))}
      </span>

      <input
        type="text"
        className={classes.configInput}
        value={localValue || ''}
        placeholder="MM / DD / YYYY"
        onChange={e => getDateChangeHandler(e, props.config.path, props.handleChange)}
        autoComplete="chrome-off"
      />
    </div>
  );
};

const StringInput: React.FC<IConfigComponent> = props => {
  if (!props.config) return null;

  const getStringChangeHandler = (
    e: React.ChangeEvent<HTMLInputElement>,
    path: string,
    handleChange: IConfigComponent['handleChange'],
  ) => {
    const value = e.target.value;
    const arrayIndex = props.config.arrayIndex;
    handleChange(path, value, arrayIndex);
  };

  // eslint-disable-next-line
  const classes = useStyles();
  return (
    <div
      className={classes.stringInputConfig}
      key={props.config.name}
      onMouseEnter={() => props.handleFocus(props.config)}
      onMouseLeave={() => props.handleFocus(undefined)}
    >
      <span
        className={classes.label}
        style={{
          opacity:
            props.config.displayLabel && props.config.displayLabel.includes('(deprecated)')
              ? 0.5
              : 1,
        }}
      >
        {props.config.displayLabel
          ? props.config.displayLabel
          : _.upperFirst(_.lowerCase(_.startCase(props.config.name)))}
      </span>

      <input
        className={classes.stringInputConfigInput}
        type="text"
        value={props.config.value || ''}
        onChange={e => getStringChangeHandler(e, props.config.path, props.handleChange)}
        autoComplete="chrome-off"
      />
    </div>
  );
};

const Select: React.FC<IConfigComponent> = props => {
  // eslint-disable-next-line
  const classes = useStyles();

  const selectValue = props.config.value || props.config.default || 'nothing-selected';

  const getSelectChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
    path: string,
    handleChange: IConfigComponent['handleChange'],
  ) => {
    const value = e.target.value;
    const arrayIndex = props.config.arrayIndex;
    handleChange(path, value, arrayIndex);
  };

  return (
    <div
      className={classes.config}
      key={props.config.name}
      onMouseEnter={() => props.handleFocus(props.config)}
      onMouseLeave={() => props.handleFocus(undefined)}
    >
      <span
        className={classes.label}
        style={{
          opacity:
            props.config.displayLabel &&
            props.config.displayLabel &&
            props.config.displayLabel.includes('(deprecated)')
              ? 0.5
              : 1,
        }}
      >
        {props.config.displayLabel
          ? props.config.displayLabel
          : _.upperFirst(_.lowerCase(_.startCase(props.config.name)))}
      </span>

      <select
        className={classes.select}
        onChange={e => getSelectChange(e, props.config.path, props.handleChange)}
        value={selectValue || ''}
      >
        <option disabled value="nothing-selected">
          -- select --
        </option>

        {props.config.enumValues!.map((option, i) => (
          <option key={i}>{option}</option>
        ))}
      </select>
    </div>
  );
};

const JsonInput: React.FC<IConfigComponent & { expectArray?: boolean }> = props => {
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  const getStringChangeHandler = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
    path: string,
    handleChange: IConfigComponent['handleChange'],
  ) => {
    const value = e.target.value;
    const arrayIndex = props.config.arrayIndex;
    const isValid = hasJsonStructure(value, props.expectArray);
    setValid(isValid);
    // FIXME hacky: maintain duplicate state to avoid cursor issues
    setLocalValue(value);
    handleChange(path, value, arrayIndex);
  };

  const stringifyValue = (json: string | Record<string, any>) => {
    return _.isObject(json) ? JSON.stringify(json, undefined, 2) : json;
  };

  const formatCompValue = (json: string | Record<string, any>) => {
    const strValue = stringifyValue(json);
    if (hasJsonStructure(strValue, props.expectArray)) {
      return JSON.stringify(JSON.parse(strValue), undefined, 2);
    }
    return strValue;
  };

  const [localValue, setLocalValue] = React.useState(stringifyValue(props.config.value));
  // FIXME hacky: state can be updated outside of the input, ie when removing from array
  React.useEffect(() => {
    const formattedConfigVal = formatCompValue(props.config.value);
    if (formatCompValue(localValue) !== formattedConfigVal) {
      setLocalValue(formattedConfigVal);
    }
  }, [props.config.value]);
  const [jsonVisible, toggleVisibility] = React.useState(false);
  const [validJson, setValid] = React.useState(false);

  const hasJsonStructure = (str: string, expectArray?: boolean): boolean => {
    if (typeof str !== 'string') return false;
    try {
      const result = JSON.parse(str);
      const type = Object.prototype.toString.call(result);
      return (!expectArray && type === '[object Object]') || type === '[object Array]';
    } catch (err) {
      return false;
    }
  };

  const prettify = () => {
    const obj = JSON.parse(localValue);
    const pretty = JSON.stringify(obj, undefined, 2);
    setLocalValue(pretty);
  };

  // eslint-disable-next-line
  const classes = useStyles();

  return (
    <div
      className={classes.config}
      style={{ display: 'block' }}
      key={props.config.name}
      onMouseEnter={() => props.handleFocus(props.config)}
      onMouseLeave={() => props.handleFocus(undefined)}
    >
      <div style={{ display: 'flex', justifyContent: 'spaceBetween' }}>
        <span
          className={classes.label}
          style={{
            opacity:
              props.config.displayLabel &&
              props.config.displayLabel.substr(props.config.displayLabel.length - 12) ===
                '(deprecated)'
                ? 0.5
                : 1,
          }}
        >
          {props.config.displayLabel
            ? props.config.displayLabel
            : _.upperFirst(_.lowerCase(_.startCase(props.config.name)))}
        </span>
        <button
          className={classes.addButton}
          style={{ color: '#404040' }}
          onClick={() => {
            toggleVisibility(!jsonVisible);
            setTimeout(() => {
              textAreaRef.current?.focus();
            }, 100);
          }}
        >
          {jsonVisible ? 'Hide JSON' : 'Show JSON'}
        </button>
      </div>
      {jsonVisible && (
        <div className={classes.jsonWrapper}>
          <textarea
            className={classes.jsonInput}
            ref={textAreaRef}
            value={localValue || ''}
            onChange={e => getStringChangeHandler(e, props.config.path, props.handleChange)}
          />
          <button
            className={classes.prettifyButton}
            onClick={() => prettify()}
            disabled={!validJson}
          >
            Prettify
          </button>
        </div>
      )}
    </div>
  );
};

const MultilineInput: React.FC<IConfigComponent> = props => {
  const getStringChangeHandler = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
    path: string,
    handleChange: IConfigComponent['handleChange'],
  ) => {
    const value = e.target.value;
    const arrayIndex = props.config.arrayIndex;

    // FIXME hacky: maintain duplicate state to avoid cursor issues
    setLocalValue(value);
    handleChange(path, value, arrayIndex);
  };

  const [localValue, setLocalValue] = React.useState(props.config.value);
  // FIXME hacky: state can be updated outside of the input, ie when removing from array
  React.useEffect(() => {
    if (localValue !== props.config.value) {
      setLocalValue(props.config.value);
    }
  }, [props.config.value]);
  const [textAreaVisible, toggleVisibility] = React.useState(false);

  // eslint-disable-next-line
  const classes = useStyles();

  return (
    <div
      className={classes.config}
      style={{ display: 'block' }}
      key={props.config.name}
      onMouseEnter={() => props.handleFocus(props.config)}
      onMouseLeave={() => props.handleFocus(undefined)}
    >
      <div style={{ display: 'flex', justifyContent: 'spaceBetween' }}>
        <span
          className={classes.label}
          style={{
            opacity:
              props.config.displayLabel &&
              props.config.displayLabel.substr(props.config.displayLabel.length - 12) ===
                '(deprecated)'
                ? 0.5
                : 1,
          }}
        >
          {props.config.displayLabel
            ? props.config.displayLabel
            : _.upperFirst(_.lowerCase(_.startCase(props.config.name)))}
        </span>
        <button
          className={classes.addButton}
          style={{ color: '#404040' }}
          onClick={() => toggleVisibility(!textAreaVisible)}
        >
          {textAreaVisible ? 'Hide TextArea' : 'Expand TextArea'}
        </button>
      </div>
      {textAreaVisible && (
        <div className={classes.multilineWrapper}>
          <textarea
            className={classes.multilineInput}
            value={localValue || ''}
            onChange={e => getStringChangeHandler(e, props.config.path, props.handleChange)}
          />
        </div>
      )}
    </div>
  );
};

const Toggle: React.FC<IConfigComponent> = props => {
  if (!props.config) return null;

  const getToggleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    path: string,
    handleChange: IConfigComponent['handleChange'],
  ) => {
    const arrayIndex = props.config.arrayIndex;
    handleChange(path, e.target.checked, arrayIndex);
  };

  const getDefaultEnableBenchmark = (config: IConfig) => {
    return (
      config.path === 'configSettingsValues.responseLinterConfigRules.enableBenchmarks' &&
      config.value === undefined
    );
  };

  // eslint-disable-next-line
  const classes = useStyles();

  return (
    <div
      className={classes.config}
      key={props.config.name}
      onMouseEnter={() => props.handleFocus(props.config)}
      onMouseLeave={() => props.handleFocus(undefined)}
    >
      <span
        className={classes.label}
        style={{
          opacity:
            props.config.displayLabel &&
            props.config.displayLabel &&
            props.config.displayLabel.includes('(deprecated)')
              ? 0.5
              : 1,
        }}
      >
        {props.config.displayLabel
          ? props.config.displayLabel
          : _.upperFirst(_.lowerCase(_.startCase(props.config.name)))}
      </span>
      <div>
        <label className={classes.switch}>
          <input
            className={classes.input}
            type="checkbox"
            checked={getDefaultEnableBenchmark(props.config) || props.config.value || false}
            onChange={e => getToggleChange(e, props.config.path, props.handleChange)}
          />
          <span className={classes.slider} />
        </label>
      </div>
    </div>
  );
};

// for arrays: turn {children} into array of configs
const getChildren = (children: Record<string, IBaseConfig>, path: string): IConfig[] => {
  const childrenArray: IConfig[] = [];
  const copyChildren = _.cloneDeep(children);
  // add path & name
  Object.keys(copyChildren).map(function (key) {
    copyChildren[key].name = key;
    copyChildren[key].path = path + '.' + key;
    // convert from IBaseConfig to IConfig
    const fullConfig = copyChildren[key] as any as IConfig;
    childrenArray.push(fullConfig);
  });
  return childrenArray;
};

const Array: React.FC<IConfigComponent> = props => {
  const [activeIndex, setActive] = React.useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);

  // get structure of children; array config will always have children, unless it's a primitive / non-schema array
  const structure = props.config.children!;
  const structureChildren = getChildren(structure, props.config.path);

  // this feels a bit messy
  const getExistingValues = (values: Array<Record<string, any>>) => {
    if (!values) return;
    const existingValues: Array<Array<IConfig>> = [];
    values.map(set => {
      // set is object; I need to get the key
      const valueConfigs = Object.keys(set);
      // fill in config structure with existing values
      const newSet: Array<IConfig> = [];
      const emptyClone = _.cloneDeep(structureChildren);
      emptyClone.map(childConfig => {
        valueConfigs.map(configName => {
          if (childConfig.name === configName) {
            // fill
            childConfig.value = set[configName];
          }
        });
        newSet.push(childConfig);
      });

      existingValues.push(newSet);
    });

    return existingValues;
  };

  const existingValues = getExistingValues(props.config.value);

  // intercept normal change handler
  const interceptChange = (
    path: string,
    inputValue: any,
    // I think this throws lint error but it's necessary
    // eslint-disable-next-line
    arrayIndex?: number,
  ): void => {
    // this shouldn't ever happen...
    if (arrayIndex === undefined) {
      return;
    }
    // set entire new values array
    const newArrayValue = [...props.config.value];
    const suffix = path.substring(path.lastIndexOf('.') + 1);
    newArrayValue[arrayIndex][suffix] = inputValue;
    // adjust path for arrays
    const properPath = path.substring(0, path.lastIndexOf('.'));
    props.handleChange(properPath, newArrayValue);
  };

  const addNew = () => {
    const newArrayValue = props.config.value ? [...props.config.value] : [];
    // add empty obj; structure will fill out in next render (existingValues)
    newArrayValue.push({});
    props.handleChange(props.config.path, newArrayValue);
  };

  const remove = () => {
    const newArrayValue = [...props.config.value];
    newArrayValue.splice(activeIndex, 1);
    props.handleChange(props.config.path, newArrayValue);
    if (activeIndex > 0) {
      handleNav(-1);
    }
  };

  const handleNav = (change: number) => {
    setActive(activeIndex + change);
  };

  const pixelOffset = useMemo(() => {
    // Use if ref hasn't loaded, or some other CSS issue
    const emergencyValue = -302 * activeIndex;
    // If no ref has loaded, use hacky old value
    if (!carouselRef.current) {
      return emergencyValue;
    }

    const targetElement = carouselRef.current.children[activeIndex] as HTMLElement;
    // If missing child somehow, use hacky old value
    if (!targetElement) {
      return emergencyValue;
    }

    return -1 * targetElement.offsetLeft;
  }, [activeIndex, carouselRef.current]);

  // eslint-disable-next-line
  const classes = useStyles();

  return (
    <div>
      <div
        className={classes.config}
        key={props.config.name}
        onMouseEnter={() => props.handleFocus(props.config)}
        onMouseLeave={() => props.handleFocus(undefined)}
      >
        <span
          className={classes.label}
          style={{
            opacity:
              props.config.displayLabel &&
              props.config.displayLabel.substr(props.config.displayLabel.length - 12) ===
                '(deprecated)'
                ? 0.5
                : 1,
          }}
        >
          {props.config.displayLabel
            ? props.config.displayLabel
            : _.upperFirst(_.lowerCase(_.startCase(props.config.name)))}{' '}
          ({existingValues ? existingValues.length : 0})
        </span>

        <button className={classes.addButton} onClick={addNew}>
          Add
        </button>
      </div>

      {existingValues && existingValues.length > 0 && (
        <div className={classes.carouselWrapper}>
          <div className={classes.navWrapper}>
            {existingValues.length > 1 && (
              <div className={classes.flexWrapper}>
                <button
                  className={classes.navButton}
                  onClick={() => handleNav(-1)}
                  disabled={activeIndex === 0}
                >
                  <FontAwesomeIcon icon="arrow-left" />
                </button>

                <span className={classes.count}>
                  <input
                    className={classes.configInput}
                    type="number"
                    step={1}
                    min={1}
                    max={existingValues.length + 1}
                    value={activeIndex + 1}
                    onChange={e =>
                      setActive(
                        _.clamp(_.toNumber(e.target.value) - 1, 0, existingValues.length - 1),
                      )
                    }
                  />
                  &nbsp;<span>of {existingValues.length}</span>
                </span>

                <button
                  className={classes.navButton}
                  onClick={() => handleNav(1)}
                  disabled={activeIndex === existingValues.length - 1}
                >
                  <FontAwesomeIcon icon="arrow-right" />
                </button>
              </div>
            )}

            <button className={classes.navButton} style={{ flexShrink: 0 }} onClick={remove}>
              <FontAwesomeIcon icon="trash-alt" />
            </button>
          </div>
          <div
            className={classes.carousel}
            ref={carouselRef}
            style={{ transform: `translateX(${pixelOffset}px)` }}
          >
            {existingValues.map((valueSet, index) => {
              return (
                <div key={index} className={classes.configSet}>
                  {valueSet.map((childConfig: IConfig) => {
                    childConfig.arrayIndex = index;
                    return renderInput(childConfig, undefined, interceptChange, props.handleFocus);
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// new interface to allow arrays of configs
interface ICollectionComponent {
  config: Array<IConfig>;
  changes: Record<string, any> | undefined;
  handleChange: (path: string, value: any, arrayIndex?: number) => void;
  handleFocus: (config: IConfig | undefined) => void;
}

// <Collection /> is purely for UI reasons
const Collection: React.FC<ICollectionComponent> = props => {
  // eslint-disable-next-line
  const classes = useStyles();

  return (
    <div className={classes.partitionWrapper}>
      {props.config.map((partition: IConfig) => {
        return renderInput(partition, props.changes, props.handleChange, props.handleFocus);
      })}
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  wrapper: {
    width: '100%',
  },
  bodyWrapper: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  configsWrapper: {
    padding: 12,
    background: '#F6F5FA',
    display: 'inline-block',
    width: 340,
    borderRadius: 8,
    marginRight: 32,
    flexShrink: 0,
  },
  stringInputConfig: {
    padding: 6,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'start',
    alignItems: 'left',
    borderRadius: 4,
    cursor: 'help',
    '&:hover': {
      background: 'rgba(0,0,0,0.04)',
    },
  },
  config: {
    padding: 6,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderRadius: 4,
    cursor: 'help',
    '&:hover': {
      background: 'rgba(0,0,0,0.04)',
    },
  },
  label: {
    fontSize: 12,
    lineHeight: '16px',
    minHeight: 24,
    display: 'flex',
    alignItems: 'center',
    marginRight: 16,
    flexGrow: 1,
    // cursor: 'help',
    borderRadius: 4,
  },
  stringInputConfigInput: {
    height: 24,
    width: '100%',
    fontSize: 12,
    lineHeight: '16px',
    border: 'solid 1px #ccc',
    borderRadius: 4,
    padding: '4px 6px',
    transition: 'border 120ms ease',
    '&:focus': {
      borderColor: '#DD45D3',
    },
    '&::-webkit-outer-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
    '&::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
  },
  configInput: {
    height: 24,
    width: 88,
    fontSize: 12,
    lineHeight: '16px',
    border: 'solid 1px #ccc',
    borderRadius: 4,
    padding: '4px 6px',
    transition: 'border 120ms ease',
    '&:focus': {
      borderColor: '#DD45D3',
    },
    '&::-webkit-outer-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
    '&::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
  },
  dateInput: {
    height: 24,
    width: 88,
    fontSize: 12,
    lineHeight: '16px',
    border: 'solid 1px #ccc',
    borderRadius: 4,
    padding: '4px 6px',
    transition: 'border 120ms ease',
    '&:focus': {
      borderColor: '#DD45D3',
    },
    '&::-webkit-outer-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
    '&::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
  },

  jsonWrapper: {
    marginTop: 6,
    position: 'relative',
    '&:hover > button': {
      opacity: 1,
    },
  },
  jsonInput: {
    width: '100%',
    height: 128,
    resize: 'none',
    border: 'solid 1px #ccc',
    borderRadius: 4,
    fontSize: 11,
    fontFamily: theme.typography.fontFamilyMono,
    padding: '4px 6px',
    '&:focus': {
      borderColor: '#DD45D3',
    },
  },
  multilineWrapper: {
    marginTop: 6,
    position: 'relative',
    '&:hover > button': {
      opacity: 1,
    },
  },
  multilineInput: {
    width: '100%',
    height: 128,
    resize: 'none',
    border: 'solid 1px #ccc',
    borderRadius: 4,
    fontSize: 11,
    fontFamily: theme.typography.fontFamily,
    padding: '4px 6px',
    '&:focus': {
      borderColor: '#DD45D3',
    },
  },
  prettifyButton: {
    position: 'absolute',
    right: 8,
    top: 8,
    background: '#f2f2f2',
    outline: 'none',
    border: 'none',
    padding: '2px 4px',
    fontSize: 12,
    borderRadius: 4,
    cursor: 'pointer',
    opacity: 0,
    transition: 'all 150ms ease-out',
    '&:hover': {
      opacity: 1,
      background: '#e6e6e6',
    },
    '&:disabled': {
      color: '#ccc',
      cursor: 'not-allowed',
    },
  },
  rightContent: {
    width: 350,
    wordBreak: 'break-word',
  },
  description: {
    lineHeight: 1.6,
    paddingLeft: 8,
  },
  // toggle
  switch: {
    position: 'relative',
    display: 'inline-block',
    width: 32,
    height: 20,
    '& input': {
      opacity: 0,
      width: 0,
      height: 0,
    },
  },
  slider: {
    position: 'absolute',
    cursor: 'pointer',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#C5C4C8',
    borderRadius: 24,
    transition: '200ms',
    '&::before': {
      position: 'absolute',
      content: '""',
      height: 14,
      width: 14,
      left: 3,
      bottom: 3,
      backgroundColor: 'white',
      borderRadius: '50%',
      transition: '200ms',
    },
  },
  input: {
    '&:checked + $slider': {
      backgroundColor: '#DD45D3',
    },
    '&:checked + $slider:before': {
      transform: 'translateX(12px)',
    },
  },
  select: {
    height: 24,
    width: 88,
    minWidth: 88,
    padding: '4px 20px 4px 6px',
    fontSize: 12,
    lineHeight: '16px',
    border: '1px solid #CCC',
    borderRadius: 4,
    appearance: 'none',
    backgroundImage: 'url(https://d30y9cdsu7xlg0.cloudfront.net/png/221782-200.png)',
    backgroundPosition: '96% center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 12,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    '&:hover': {
      borderColor: 'grey',
    },
  },
  dictionary: {
    borderLeft: 'solid 2px pink',
    borderColor: 'rgba(221, 69, 211, 0.5)',
    paddingLeft: 8,
    marginTop: 16,
    position: 'relative',
  },
  dictText: {
    lineHeight: 1.6,
    margin: 0,
    marginBottom: 8,
  },
  dictLabel: {
    color: '#808080',
  },
  addButton: {
    background: 'white',
    outline: 'none',
    border: 'none',
    color: '#DD45D3',
    height: 24,
    padding: '0 8px',
    fontSize: 12,
    fontWeight: 500,
    borderRadius: 4,
    boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
    cursor: 'pointer',
    transition: 'box-shadow 150ms ease-out',
    '&:hover': {
      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
    },
  },
  carouselWrapper: {
    overflow: 'hidden',
    position: 'relative',
    margin: 6,
    border: 'solid 1px rgba(0,0,0,0.1)',
    borderRadius: 8,
  },
  carousel: {
    display: 'flex',
    transition: 'transform 300ms ease-out',
  },
  configSet: {
    width: '100%',
    flexShrink: 0,
    padding: 8,
    paddingTop: 0,
  },
  navWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 16px',
    paddingLeft: 8,
    marginBottom: 6,
    marginTop: 12,
    overflowX: 'scroll',
  },
  count: {
    fontSize: 12,
    lineHeight: '16px',
    padding: '0 6px',
    color: 'black',
    opacity: 0.5,
    flexShrink: 0,
  },
  navButton: {
    height: 24,
    width: 24,
    background: 'transparent',
    outline: 'none',
    border: 'none',
    borderRadius: 4,
    transition: 'background 150ms ease-out',
    opacity: 0.6,
    cursor: 'pointer',
    '&:hover': {
      background: 'rgba(0,0,0,0.1)',
    },
  },
  flexWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  dot: {
    height: 6,
    width: 6,
    margin: '0 3px',
    borderRadius: '50%',
    background: 'black',
    transition: 'opacity 300ms ease-out',
  },
  partitionWrapper: {
    borderRadius: 4,
    '&:hover': {
      background: 'rgba(0,0,0,0.03)',
    },
  },
}));
